import type { Types as AmplitudeTypes } from "@amplitude/analytics-browser";
import { debounce } from "@solid-primitives/scheduled";
import type { Navigator } from "@solidjs/router";
import { isAxiosError } from "axios";
import { createEffect, on, untrack } from "solid-js";
import { signOut } from "supertokens-web-js/lib/build/recipe/thirdpartyemailpassword";
import type { Primitive } from "zod";
import { config } from "~/js/network/network-handler";
import { addPrefixToKeys } from "~/js/utilities/addPrefixToKeys";
import EventAnalytics, { GrayLog } from "~/js/utilities/EventAnalytics";
import { getUserStore, setUserStore } from "~/solidJs/entities/HeaderMain/models";
import { PaymentApi } from "../api/PaymentApi";
import { UserApi } from "../api/user";
import { CustomRuntimeError } from "../models/errors/runtimeErrors";
import { newIdenifyEvent, sendIdentifyEvent } from "./amplitudeHelpers";
import { getCookie } from "./cookiesHelpers";
import { parseDateToReadableString } from "./dateHelpers";
import { useOnDeferredUpdate } from "./effectsHelpers";
import { getPrivateMessageFromError } from "./errorHelpers";
import { foundDiffBetweenTwoPrimitiveObjects, type primitives } from "./objectHelpers";
import { capitalizeFirstLetter } from "./stringHelpers";
import { DebugFlags, isDebugFlag } from "./testHelpers";
import {
	type IUser,
	SubscriptionPlanStatusEnum,
	type UserOnlineInformation,
	UserSubscriptionPlanEnum
} from "./types";
import type { FilterObjectByValue } from "./types/ObjectHelpersTypes";

export const DEFAULT_DISABLED_GROUPS = {
	free: [
		UserSubscriptionPlanEnum.Free,
		UserSubscriptionPlanEnum.Legacy_Freemium,
		UserSubscriptionPlanEnum.Legacy_Trialing
	]
} as const satisfies Record<string, UserSubscriptionPlanEnum[]>;

export const getIsAccesDisabled = (...disabledGroups: UserSubscriptionPlanEnum[]) => {
	const group = getUserStore()?.group;

	if (!group) return true;
	for (const disabledGroup of disabledGroups) {
		if (group === disabledGroup) return true;
	}
	return false;
};
export function isEmailVerified(tokenPayload?: Record<string, any> | null) {
	return tokenPayload?.["st-ev"]?.v;
}

export async function getUserInfo(): Promise<IUser | null> {
	const refreshToken = getCookie("st-refresh-token");
	if (refreshToken === null) {
		return null;
	}
	const session = await UserApi.getUserInitInfo();
	if (!session) return null;

	return session;
}

export async function getUserOnlineInfo(props?: {
	retryDelay?: number;
}): Promise<UserOnlineInformation | null> {
	const refreshToken = getCookie("st-refresh-token");
	if (refreshToken === null) {
		return null;
	}
	const session = await UserApi.getUserOnlineInfo(props);
	if (!session) return null;

	return session;
}

export async function getUserAndSetStore() {
	const user = await getUserInfo();
	setUserStore(user);
	return user;
}

export async function revalidateUserOnlineInfo(props?: { retryDelay?: number }) {
	const userOnline = await getUserOnlineInfo(props);
	if (!userOnline) return;
	setUserStore((prev) => {
		if (!prev) return userOnline as IUser;
		return { ...prev, ...userOnline } as IUser;
	});
	return userOnline;
}

export const debounceRevalidateUserOnlineInfo = debounce(
	() => revalidateUserOnlineInfo().catch(() => null),
	1000
);

export function isUserInited(user: IUser | null) {
	return user && !!user.id;
}

export function createRevalidateUserOnlineInfoInterval(modalMethods?: {
	show: () => void;
	close: () => void;
}) {
	let interval: NodeJS.Timeout;
	let lastRevalidation = 0;
	let intervalTimeout = 10000;
	let isInErrorState = false;
	const onRevalidateOnlineInfo = async () => {
		const startRevalidating = Date.now();
		if (startRevalidating - lastRevalidation < intervalTimeout) {
			return;
		}

		lastRevalidation = Date.now();
		try {
			await revalidateUserOnlineInfo({ retryDelay: isInErrorState ? 5000 : 1000 });
			intervalTimeout = 10000;
			if (isInErrorState) {
				modalMethods?.close();
			}
			isInErrorState = false;
		} catch (e) {
			let error = e;
			if (e instanceof CustomRuntimeError) {
				error = e.cause;
			}
			if (isAxiosError(error) && (error.status === 500 || error.code === "ERR_NETWORK")) {
				intervalTimeout = 20000;
				if (!isInErrorState) {
					modalMethods?.show();
				}
				isInErrorState = true;
			}
		}
	};

	const start = () => {
		interval = setInterval(onRevalidateOnlineInfo, 10000);
		window.addEventListener("focus", onRevalidateOnlineInfo);
	};

	const stop = () => {
		clearInterval(interval);
		window.removeEventListener("focus", onRevalidateOnlineInfo);
	};

	return { start, stop };
}

export function isAuthenticated(user?: IUser | null): boolean {
	return !!user;
}

export function verifyUser() {
	setUserStore((prev) => {
		if (!prev) return prev;
		prev["st-ev"] = {
			v: true,
			t: Date.now()
		};
		return { ...prev };
	});
}

export function isUserNeedToSetupAccount(user: IUser | null): boolean {
	return (
		isDebugFlag(DebugFlags.SetupAccontDebug) ||
		(!!user && (user.questionnaire === null || user.questionnaire === false))
	);
}

export function isUserHasAccess(user: IUser | null, accessMap: Record<string, boolean>): boolean {
	if (!user) {
		return false;
	}
	return user.group in accessMap;
}
/**
 * @description gets payments from the server and send info to analitycs system
 */
export async function registratePaymentsInAnalitycs() {
	if (config?.IS_PRODUCTION !== "true") {
		return;
	}
	let payments: Awaited<ReturnType<typeof PaymentApi.getUserPayments>> | undefined;
	try {
		payments = await PaymentApi.getUserPayments();
	} catch (e) {
		console.error(e);
		EventAnalytics.sendErrorInfo("get-payments-error", {
			message: getPrivateMessageFromError(e)
		});
		return;
	}
	if (!payments?.length) {
		return;
	}
	try {
		for (const payment of payments) {
			try {
				GrayLog.debug({
					log_name: "purchase",
					message: "send purchase event",
					isNeedToSendToAnalitics: false,
					price: payment.price,
					transaction_id: payment.transaction_id,
					code: payment.discount_code
				});
			} catch (_e) {}
			EventAnalytics.purchace(payment.price, payment.transaction_id, payment.discount_code);
			EventAnalytics.checkoutCompleted(payment.subscription_name);
		}
	} catch (e) {
		console.error(e);
		EventAnalytics.sendErrorInfo("send-payments-error", {
			message: getPrivateMessageFromError(e)
		});
	}
}

export const isUserFree = (user?: IUser | null) => {
	return (
		user?.group === UserSubscriptionPlanEnum.Legacy_Freemium ||
		user?.group === UserSubscriptionPlanEnum.Legacy_Trialing ||
		user?.group === UserSubscriptionPlanEnum.Free
	);
};

export const isUserCanUseCredits = (user?: IUser | null) => {
	if (!user) return false;
	if (isUserFree(user)) return false;
	if (getIsUserOnLegacyPlan(user)) return false;
	return true;
};

export const getUserGenerationsAmount = (user: Partial<IUser> | null) => {
	if (!user || !user.available_runs || !user.total_runs) return 0;
	return user.available_runs;
};

export const getIsUserHasGenerations = async () => {
	const onlineInfo = await revalidateUserOnlineInfo().catch(() => null);
	if (!onlineInfo) return false;
	return getUserGenerationsAmount(onlineInfo) > 0;
};

const OAuthRestoreLocationKey = "OAuthRestoreLocationKey";

export const captureRedirectAuthLocation = () => {
	const url = new URL(location.href);
	const redirectTo = url.searchParams.get("from");
	if (!redirectTo) {
		localStorage.removeItem(OAuthRestoreLocationKey);
		return;
	}
	localStorage.setItem(OAuthRestoreLocationKey, redirectTo);
};

export const resotreRedirectAuthLocation = (navigate: Navigator) => {
	let out = false;
	const redirectTo = localStorage.getItem(OAuthRestoreLocationKey);
	if (redirectTo) {
		navigate(redirectTo, { replace: true });
		out = true;
	}

	localStorage.removeItem(OAuthRestoreLocationKey);
	return out;
};

const disabledPropertiesLookUp = {
	status: true,
	start_time: true,
	end_time: true,
	"st-ev": true,
	subscription: true,
	group: true,
	next_payment: true,
	credits: true
} as const satisfies Partial<Record<keyof IUser, boolean>>;

export const getNonDisabledUserFields = (user: IUser) => {
	const record = {} as FilterObjectByValue<IUser, primitives>;
	for (const key in user) {
		if (
			key in disabledPropertiesLookUp ||
			typeof (user as any)[key] === "object" ||
			Array.isArray((user as any)[key])
		) {
			continue;
		}
		// @ts-expect-error
		record[key] = (user as any)[key];
	}
	return record;
};

/**
 * Remember last user info in local storage
 * if there is no user, return last remembered user
 */
export const rememberDangerousUser = (user?: IUser): IUser | null => {
	if (!user) {
		const userFromLocalStorage = localStorage.getItem("user");
		if (userFromLocalStorage) {
			try {
				const parsedUser = JSON.parse(userFromLocalStorage);
				if (parsedUser && typeof parsedUser === "object") {
					return parsedUser as IUser;
				}
			} catch (_error) {}
		}
		return null;
	}
	localStorage.setItem("user", JSON.stringify(user));

	return user;
};

export const getAmplitudeReadyObject = <ObjT extends Record<string, Primitive>>(
	obj: ObjT
): FilterObjectByValue<ObjT, AmplitudeTypes.ValidPropertyType> => {
	const out = {} as FilterObjectByValue<ObjT, AmplitudeTypes.ValidPropertyType>;
	for (const key in obj) {
		const value = obj[key];
		if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
			// @ts-expect-error
			out[key] = value;
		}
	}
	return out;
};

export const createUserGroupChangeEffect = (props: {
	onGroupChange: (user: IUser | null) => void;
}) => {
	createEffect(
		on(
			() => getUserStore()?.group,
			(group, _, prev) => {
				if (!prev || !group || group === prev) return group;
				props.onGroupChange(getUserStore());
				return group;
			},
			{ defer: true }
		),
		untrack(() => getUserStore()?.group)
	);
};

export const createUserIdenifyEffect = () => {
	createEffect((prev: FilterObjectByValue<IUser, primitives> | undefined | null) => {
		const user = getUserStore();
		if (!user) return user;
		const primitveUser = getNonDisabledUserFields(user);
		if (primitveUser.banned_at) {
			signOut();
			document.querySelector("#banned-banner")?.classList?.remove("hidden");
			document.querySelector("#banned-banner")?.classList?.add("flex");
			return;
		}
		const prefixedUser = addPrefixToKeys(primitveUser, "user_");
		let identifyEvent: AmplitudeTypes.Identify;
		if (prev) {
			const diff = foundDiffBetweenTwoPrimitiveObjects(
				addPrefixToKeys(prev, "user_"),
				prefixedUser
			);
			if (diff.length < 1) return primitveUser;
			identifyEvent = newIdenifyEvent(getAmplitudeReadyObject(Object.fromEntries(diff)));
			if (config.FRONT_CANARY) {
				identifyEvent.set("isCanary", config.FRONT_CANARY);
			}
		} else {
			identifyEvent = newIdenifyEvent(getAmplitudeReadyObject(prefixedUser));
			if (config.FRONT_CANARY) {
				identifyEvent.set("isCanary", config.FRONT_CANARY);
			}
		}
		sendIdentifyEvent(identifyEvent, user);
		if (config.IS_PRODUCTION !== "true") {
			console.log("send-idenify", identifyEvent);
		}

		return primitveUser;
	});
};

export const isUserFromRussia = (user: IUser | null) => {
	return isGeoRussia(user?.geo);
};

export const isGeoRussia = (geo?: string | null) => {
	return geo === "Russia";
};

export const isNeedToShowGenerationLimitsCounter = () => {
	const user = getUserStore();
	if (isUserFree(user)) {
		return true;
	}
	return !!user?.become_available_at;
};

export const isUserFromAppSumo = (user: IUser | null) => {
	return user?.group.startsWith("app_sumo");
};

export const getAvatarLetter = (user: IUser | null) => {
	if (!user) return "";
	return user?.email?.[0].toUpperCase() || "";
};

export const getUserPlanName = (user: IUser | null) => {
	if (!user) return "";
	return user?.subscription_name || capitalizeFirstLetter(user?.group || "");
};

export const getUserName = (user: IUser | null) => {
	if (!user) return "";
	return user?.email.slice(0, 5);
};

export const getUserCreditsBalance = (user: IUser | null) => {
	if (!user) return 0;
	if (getIsUserInfinity(user)) {
		return getUserCreditsTotal(user);
	}
	return user?.credits?.credits_balance || 0;
};

export const getUserCreditsTotal = (user: IUser | null) => {
	if (!user) return 0;
	return (
		user?.credits?.credits_balance_breakdowns.reduce(
			(acc, item) => acc + item.credits_received,
			0
		) || 0
	);
};

export const getUserCreditsRefreshText = (user: IUser | null) => {
	if (!user) return "";
	return `Your ${user.credits.count_credits_by_sub} credits will refresh on ${getCreditsRefreshReadebleString(user)}`;
};

export const getNextBillingText = (user: IUser | null) => {
	if (!user) return "";
	if (isUserFromAppSumo(user) && getIsUserOnLegacyPlan(user)) {
		return "Your subscription is Active";
	}
	const isFree = isUserFree(user);
	const textParts: string[] = [];

	if (user.credits.expiration_date) {
		const formattedDate = new Date(user.credits.expiration_date).toLocaleDateString("en-US", {
			month: "long",
			day: "numeric"
		});
		if (isFree) {
			textParts.push(`Your credits refresh weekly on ${formattedDate}.`);
		} else if (user.group === UserSubscriptionPlanEnum.AppSumoTier4) {
			textParts.push(`Your credits refresh on ${formattedDate}.`);
		} else {
			textParts.push(`Your credits refresh monthly on ${formattedDate}.`);
		}
	}

	if (user.next_payment) {
		const [dateStr, amount] = Object.entries(user.next_payment)[0] || [];

		if (dateStr && amount !== undefined) {
			const formattedDate = new Date(dateStr).toLocaleDateString("en-US", {
				month: "long",
				day: "numeric",
				year: "numeric"
			});
			if (user.subscription === "yookassa") {
				textParts.push(`Next billing date is ${formattedDate} for ${amount}₽`);
			} else {
				textParts.push(`Next billing date is ${formattedDate} for $${amount}`);
			}
		}
	}

	return textParts.join(" ");
};
export const getNextBillingLegacyText = (user: IUser | null) => {
	if (!user || !getIsUserOnLegacyPlan(user)) return "";
	if (isUserFromAppSumo(user)) {
		return "Your subscription is Active";
	}
	const textParts: string[] = [];
	const nextInfo = user.upgrade_info;
	const upgradeText = nextInfo?.new_plan ? ` for ${nextInfo.new_plan} plan` : "";
	if (user.next_payment) {
		const [dateStr, amount] = Object.entries(user.next_payment)[0] || [];

		if (dateStr && amount !== undefined) {
			const formattedDate = new Date(dateStr).toLocaleDateString("en-US", {
				month: "long",
				day: "numeric"
			});
			if (user.subscription === "yookassa") {
				textParts.push(
					`Your subscription will get renewed on ${formattedDate} for ${amount}₽${upgradeText}`
				);
			} else {
				textParts.push(
					`Your subscription will get renewed on ${formattedDate} for $${amount}${upgradeText}`
				);
			}
		}
	}

	return textParts.join(" ");
};

export const getCreditsRefreshReadebleString = (user: IUser | null) => {
	if (!user) return "";

	if (user.credits.expiration_date) {
		const formattedDate = new Date(user.credits.expiration_date).toLocaleDateString("en-US", {
			month: "long",
			day: "numeric"
		});
		return formattedDate;
	}

	return "";
};

export const getUserEndDate = (user: IUser | null): string => {
	if (!user?.end_time) return "";
	return parseDateToReadableString(new Date(user.end_time));
};

export const getIsUserInfinity = (user: IUser | null) => {
	if (!user) return false;
	return user.credits.is_infinity;
};

export const getIsUserOnLegacyPlan = (user?: IUser | null) => {
	if (!user) return false;
	return (
		user.plan_status === SubscriptionPlanStatusEnum.Old ||
		user.plan_status === SubscriptionPlanStatusEnum.Inactive
	);
};

export const useOnUserPropertyUpdate = (
	selector: (user: IUser | null) => Primitive,
	onUpdate: (user: IUser | null) => void
) => {
	useOnDeferredUpdate(
		() => selector(getUserStore()),
		() => {
			onUpdate(getUserStore());
		}
	);
};

export const mutateUserCredits = (amount: number) => {
	setUserStore((prev) => {
		if (!prev) return prev;
		return {
			...prev,
			credits: {
				...(prev.credits || {}),
				credits_balance: prev.credits.credits_balance + amount
			}
		};
	});
};

export const isUsercanRunPricedTask = (price: number, user: IUser | null) => {
	if (!user) return false;
	if (user.credits.is_infinity) return true;
	return getUserCreditsBalance(user) >= price;
};

export const getTariffTransitionText = (user: IUser | null) => {
	if (!user || !user.upgrade_info?.upgrade_ts || !user.upgrade_info?.new_plan) return null;
	return `${user.upgrade_info.new_plan} from ${parseDateToReadableString(new Date(user.upgrade_info.upgrade_ts))}`;
};

export const getTariffTransitionDeltailedText = (user: IUser | null) => {
	if (!user || !user.upgrade_info?.upgrade_ts || !user.upgrade_info.new_plan) return null;
	return `Your subscription is active until ${parseDateToReadableString(new Date(user.upgrade_info.upgrade_ts))} and then will switch to ${user.upgrade_info.new_plan} plan.`;
};

export const getUserSubscriptionLength = (user: IUser | null) => {
	if (!user) return null;
	return user.sub_length;
};

export const getIsSaveFunctionalityAvailable = (user: IUser | null) => {
	if (!user) return false;
	return !isUserFree(user);
};
